import { z } from 'zod';
/**
 * The board, the army and the power ladder.
 *
 * Gera is the hidden-rank venue (design spec §9 G6): two armies of 21 standees
 * face each other across a 9-wide outpost desk, every rank concealed behind an
 * identical capiz-and-brass back, and the only way to learn anything about an
 * enemy piece is to spend a piece finding out.
 *
 * The vocabulary here is ours. The mechanics are a classic; the names, the
 * insignia and the copy are not borrowed from anybody — generic military grades
 * only, per the IP guardrails in design spec §9 G6 and PRD §5/O2.
 */
export const ROWS = 8;
export const COLS = 9;
export const CELLS = ROWS * COLS;
/** Pieces per side. 5 heneral + 7 opisyal + 6 tanod + 2 tiktik + 1 watawat. */
export const ARMY_SIZE = 21;
/**
 * Every rank, strongest first.
 *
 * `heneral-5` … `heneral-1` are the five-star ladder, `opisyal-7` … `opisyal-1`
 * the seven officer grades, `tanod` the six watchmen who make up the line,
 * `tiktik` the two informers, and `watawat` the flag the whole game is about.
 *
 * Order is display order, NOT strength — strength is POWER, because the two
 * disagree: a tiktik sits second from the bottom of this list and beats every
 * heneral on the board.
 */
export const RANKS = [
    'heneral-5',
    'heneral-4',
    'heneral-3',
    'heneral-2',
    'heneral-1',
    'opisyal-7',
    'opisyal-6',
    'opisyal-5',
    'opisyal-4',
    'opisyal-3',
    'opisyal-2',
    'opisyal-1',
    'tanod',
    'tiktik',
    'watawat',
];
/** Wire-level validation for anything a client claims is a rank. */
export const RankCode = z.enum(RANKS);
const RANK_SET = new Set(RANKS);
export function isRank(x) {
    return typeof x === 'string' && RANK_SET.has(x);
}
/** The flag. Loses to everything — and winning the game is what it is for. */
export const FLAG = 'watawat';
/** The informer. Beats every officer and heneral, dies to a tanod. */
export const SPY = 'tiktik';
/** The line watchman. The only piece that can stop a tiktik. */
export const GRUNT = 'tanod';
/** Exactly what a legal deployment must place. Sums to ARMY_SIZE. */
export const ARMY = {
    'heneral-5': 1,
    'heneral-4': 1,
    'heneral-3': 1,
    'heneral-2': 1,
    'heneral-1': 1,
    'opisyal-7': 1,
    'opisyal-6': 1,
    'opisyal-5': 1,
    'opisyal-4': 1,
    'opisyal-3': 1,
    'opisyal-2': 1,
    'opisyal-1': 1,
    tanod: 6,
    tiktik: 2,
    watawat: 1,
};
/**
 * The straight chain: higher takes lower. This is the ladder BEFORE the three
 * exceptions in resolveClash(), and reading it on its own will get the game
 * wrong — the tiktik's 1 and the watawat's 0 are placeholders that the
 * exceptions override in both directions.
 */
export const POWER = {
    'heneral-5': 14,
    'heneral-4': 13,
    'heneral-3': 12,
    'heneral-2': 11,
    'heneral-1': 10,
    'opisyal-7': 9,
    'opisyal-6': 8,
    'opisyal-5': 7,
    'opisyal-4': 6,
    'opisyal-3': 5,
    'opisyal-2': 4,
    'opisyal-1': 3,
    tanod: 2,
    tiktik: 1,
    watawat: 0,
};
export const SEATS = [0, 1];
export function isSeat(x) {
    return x === 0 || x === 1;
}
export const other = (seat) => (seat === 0 ? 1 : 0);
// ── geometry ────────────────────────────────────────────────────────────────
//
// Flat row-major: cell = row * 9 + col, row 0 along seat 0's back edge. One flat
// array rather than an array of rows because every rule here is about ADJACENCY,
// and a single index keeps the repetition key, the deployment zone check and the
// wire format all speaking the same number.
export const rowOf = (cell) => Math.floor(cell / COLS);
export const colOf = (cell) => cell % COLS;
export const cellAt = (row, col) => row * COLS + col;
export function onBoard(cell) {
    return Number.isInteger(cell) && cell >= 0 && cell < CELLS;
}
export function pieceAt(board, cell) {
    return board[cell] ?? null;
}
/**
 * The four orthogonal neighbours, clipped at the edges: up, down, left, right.
 *
 * Fixed order, because it decides the order of legalActions() and therefore
 * which move a timed-out seat plays. Arbitrary, but a replay has to reproduce it.
 */
export function neighbours(cell) {
    const r = rowOf(cell);
    const c = colOf(cell);
    const out = [];
    if (r > 0)
        out.push(cell - COLS);
    if (r < ROWS - 1)
        out.push(cell + COLS);
    if (c > 0)
        out.push(cell - 1);
    if (c < COLS - 1)
        out.push(cell + 1);
    return out;
}
/** The three rows a seat deploys into, inclusive. Seat 0 rows 0–2, seat 1 rows 5–7. */
export function homeZone(seat) {
    return seat === 0 ? { from: 0, to: 3 * COLS - 1 } : { from: (ROWS - 3) * COLS, to: CELLS - 1 };
}
export function inHome(seat, cell) {
    const zone = homeZone(seat);
    return cell >= zone.from && cell <= zone.to;
}
/**
 * The far back rank a seat's watawat must reach. Seat 0 advances DOWN the board
 * to row 7, seat 1 advances UP to row 0 — i.e. each flag is walking into the
 * row the other army deployed on.
 */
export function flagHomeRow(seat) {
    return seat === 0 ? ROWS - 1 : 0;
}
export function countAlive(board, seat) {
    let n = 0;
    for (let i = 0; i < CELLS; i++)
        if (pieceAt(board, i)?.seat === seat)
            n += 1;
    return n;
}
/** Where a seat's watawat stands, or -1 once it has fallen. */
export function flagCell(board, seat) {
    for (let i = 0; i < CELLS; i++) {
        const pc = pieceAt(board, i);
        if (pc && pc.seat === seat && pc.rank === FLAG)
            return i;
    }
    return -1;
}
//# sourceMappingURL=pieces.js.map